import React, { useState, useEffect, useCallback } from 'react';
import bridgeService from '../../../services/bridgeService';
import { formatStaffNames, collectEngineersFromManagers } from '../../../utils/bridgeStaffDisplay';
import sensorService from '../../../services/sensorService';
import Loading from '../../../components/common/Loading';
import useNotify from '../../../hooks/useNotify';
import { useAuth } from '../../../context/AuthContext';
import { filterBridgesByRole } from '../../../utils/bridgeAccess';

const today   = new Date().toISOString().split('T')[0];
const thirtyAgo = new Date(Date.now()-30*24*60*60*1000).toISOString().split('T')[0];

export default function ReportPage() {
  const { user } = useAuth();
  const [bridges,  setBridges]  = useState([]);
  const [selected, setSelected] = useState(null);
  const [report,   setReport]   = useState(null);
  const [loading,  setLoading]  = useState(true);
  const [genLoad,  setGenLoad]  = useState(false);
  const [start,    setStart]    = useState(thirtyAgo);
  const [end,      setEnd]      = useState(today);
  const [resendingId, setResendingId] = useState(null);
  const { showToast } = useNotify();

  const selectBridge = async (id) => {
    const fallback = bridges.find((b) => b._id === id);
    try {
      const full = await bridgeService.fetchDetail(id);
      setSelected(full);
      setBridges((prev) => prev.map((b) => (b._id === id ? full : b)));
    } catch {
      if (fallback) setSelected(fallback);
    }
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const d = await bridgeService.getAll();
        const list = filterBridgesByRole(d.bridges || [], user);
        if (cancelled) return;
        setBridges(list);
        if (!list.length) return;
        try {
          const full = await bridgeService.fetchDetail(list[0]._id);
          if (!cancelled) {
            setSelected(full);
            setBridges((prev) => prev.map((b) => (b._id === full._id ? full : b)));
          }
        } catch {
          if (!cancelled) setSelected(list[0]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user]);

  const generateReport = useCallback(async () => {
    if (!selected) return;
    setGenLoad(true);
    try {
      const data = await sensorService.getReport(selected._id, { startDate:start, endDate:end });
      setReport(data.report);
    } catch (e) { showToast(e.message, 'error'); }
    finally { setGenLoad(false); }
  }, [selected, start, end, showToast]);

  useEffect(() => {
    if (selected) generateReport();
  }, [selected, generateReport]);

  const ackAlert = async (id) => {
    try {
      await sensorService.acknowledgeAlert(id);
      showToast('Alert acknowledged.');
      generateReport();
    } catch (e) { showToast(e.message, 'error'); }
  };

  const resendAlertNotification = async (id) => {
    setResendingId(id);
    try {
      await sensorService.resendNotification(id);
      showToast('Notifications resent (email / SMS / voice).');
      generateReport();
    } catch (e) {
      showToast(e.message || 'Failed to resend notification.', 'error');
    } finally {
      setResendingId(null);
    }
  };

  if (loading) return <Loading />;

  const sum     = report?.summary || {};
  const sensors = report?.sensors || selected?.sensors || [];
  const alerts  = report?.alerts  || [];

  return (
    <div>
      <div className="card">
        <div className="card-header"><i className="ti ti-file-text" /> Report</div>

        {/* Toolbar */}
        <div style={{ display:'flex', gap:8, flexWrap:'wrap', alignItems:'flex-end', marginBottom:12 }}>
          <div className="form-group">
            <label>Bridge</label>
            <select value={selected?._id || ''} onChange={(e) => selectBridge(e.target.value)}>
              {bridges.map(b=><option key={b._id} value={b._id}>{b.name}</option>)}
            </select>
          </div>
          <div className="form-group">
            <label>Start Date</label>
            <input type="date" value={start} onChange={e=>setStart(e.target.value)} />
          </div>
          <div className="form-group">
            <label>End Date</label>
            <input type="date" value={end} onChange={e=>setEnd(e.target.value)} />
          </div>
          <button className="btn btn-blue" onClick={generateReport} disabled={genLoad}>
            {genLoad ? 'Generating...' : 'Generate Report'}
          </button>
          <button className="btn btn-teal" onClick={()=>showToast('PDF export — coming soon','info')}>
            <i className="ti ti-download" /> Export PDF
          </button>
          <button className="btn btn-gray" onClick={()=>showToast('CSV export — coming soon','info')}>
            <i className="ti ti-download" /> Export CSV
          </button>
        </div>

        {genLoad ? <Loading message="Generating report..." /> : (
          <>
            {/* Summary cards */}
            <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:8, marginBottom:14 }}>
              {[
                { label:'Total Sensors',  value: sum.totalSensors  ?? sensors.length, color:'#1a6fa8', bg:'#eef4fb', border:'#b8d4ee' },
                { label:'Total Alerts',   value: sum.totalAlerts   ?? alerts.length,  color:'#d63030', bg:'#fdeaea', border:'#f5b8b8' },
                { label:'Critical Events',value: sum.criticalAlerts ?? 0,             color:'#e07020', bg:'#fff3e0', border:'#f5d5a0' },
                { label:'System Uptime',  value: (sum.uptime ?? '—') + (sum.uptime != null ? '%' : ''), color:'#0f6e56', bg:'#e8f8f0', border:'#a8ddb8' },
              ].map(m=>(
                <div key={m.label} style={{ background:m.bg, border:`1px solid ${m.border}`, borderRadius:4, padding:10, textAlign:'center' }}>
                  <div style={{ fontSize:10, color:'#555' }}>{m.label}</div>
                  <div style={{ fontSize:24, fontWeight:700, color:m.color }}>{m.value}</div>
                </div>
              ))}
            </div>

            {/* Bridge summary */}
            <div style={{ fontWeight:700, fontSize:13, color:'#1a6fa8', marginBottom:6, paddingBottom:4, borderBottom:'1px solid #eee' }}>Bridge Summary</div>
            <table style={{ marginBottom:14 }}>
              <thead><tr><th>Bridge</th><th>Country</th><th>State</th><th>City</th><th>Division</th><th>Admin</th><th>Manager</th><th>Engineer</th><th>Owner</th></tr></thead>
              <tbody>
                {selected && <tr>
                  <td><b>{selected.name}</b></td>
                  <td>{selected.country}</td><td>{selected.state}</td><td>{selected.city}</td>
                  <td>{selected.division}</td>
                  <td>{formatStaffNames(selected.admins)}</td>
                  <td>{formatStaffNames(selected.managers)}</td>
                  <td>{formatStaffNames(collectEngineersFromManagers(selected.managers))}</td>
                  <td>{formatStaffNames(selected.owners)}</td>
                </tr>}
              </tbody>
            </table>

            {/* Sensor list */}
            <div style={{ fontWeight:700, fontSize:13, color:'#1a6fa8', marginBottom:6, paddingBottom:4, borderBottom:'1px solid #eee' }}>Sensor List</div>
            <table style={{ marginBottom:14 }}>
              <thead><tr><th>#</th><th>Type</th><th>Sr. No.</th><th>Span</th><th>Girder</th><th>Location</th><th>MQTT Topic</th><th>Status</th></tr></thead>
              <tbody>
                {sensors.map((s,i)=>(
                  <tr key={s._id||i}>
                    <td>{i+1}</td>
                    <td>{s.sensorType||s.type}</td>
                    <td><code style={{ fontSize:11 }}>{s.sensorSerialNo||s.srno}</code></td>
                    <td>{s.spanNo||s.span}</td>
                    <td>{s.girderNo||s.girder}</td>
                    <td>{s.manualLocation||s.location||'—'}</td>
                    <td><code style={{ fontSize:10, color:'#888' }}>{s.mqttTopic||'—'}</code></td>
                    <td>
                      <span style={{ background: s.lastDataReceived?'#d4f7e0':'#f0f0f0', color: s.lastDataReceived?'#0a5040':'#888', padding:'2px 8px', borderRadius:10, fontSize:10 }}>
                        {s.lastDataReceived ? 'Online' : 'Pending'}
                      </span>
                    </td>
                  </tr>
                ))}
                {sensors.length === 0 && <tr><td colSpan="8" style={{ textAlign:'center', color:'#aaa', padding:16 }}>No sensors found</td></tr>}
              </tbody>
            </table>

            {/* Alert log */}
            <div style={{ fontWeight:700, fontSize:13, color:'#1a6fa8', marginBottom:6, paddingBottom:4, borderBottom:'1px solid #eee' }}>Alert Log</div>
            <p style={{ fontSize:11, color:'#666', margin:'0 0 8px' }}>
              Significant / critical rows are critical threshold events. Backend may send email, SMS, and voice to bridge Owner, Admins, Managers, and creator.
            </p>
            <table>
              <thead><tr><th>Date &amp; Time</th><th>Sensor</th><th>Variable</th><th>Value</th><th>Severity</th><th>Notified</th><th>Acknowledged</th><th>Actions</th></tr></thead>
              <tbody>
                {alerts.length ? alerts.slice(0,30).map(a=>{
                  const isCritical = a.severity === 'critical' || a.severity === 'significant';
                  const wasNotified = Boolean(
                    a.notificationSent
                    || a.notificationsSent
                    || (Array.isArray(a.notifiedChannels) && a.notifiedChannels.length > 0)
                  );
                  const channels = Array.isArray(a.notifiedChannels) && a.notifiedChannels.length
                    ? a.notifiedChannels.filter(Boolean).join(', ')
                    : (wasNotified ? 'sent' : '—');
                  const busy = resendingId === a._id;
                  return (
                  <tr key={a._id} className={a.severity==='critical'?'sev-red':a.severity==='significant'?'sev-orange':'sev-yellow'}>
                    <td>{new Date(a.timestamp).toLocaleString()}</td>
                    <td>{a.sensorSerialNo||'—'}</td>
                    <td>{a.variable||'—'}</td>
                    <td>{a.triggeredValue!=null?a.triggeredValue.toFixed(3):'—'}</td>
                    <td style={{ textTransform:'capitalize', fontWeight:600 }}>{a.severity}</td>
                    <td style={{ fontSize:11, color: isCritical ? '#8a1f11' : '#888' }}>{channels}</td>
                    <td>
                      {a.acknowledged
                        ? <span style={{ color:'#0f6e56' }}>✓ {a.acknowledgedAt ? new Date(a.acknowledgedAt).toLocaleDateString() : ''}</span>
                        : <button className="btn btn-sm btn-blue" onClick={()=>ackAlert(a._id)}>Acknowledge</button>}
                    </td>
                    <td>
                      {isCritical && (
                        <button
                          type="button"
                          className="btn btn-sm btn-gray"
                          disabled={busy}
                          title="Resend email, SMS, and voice to bridge contacts"
                          onClick={() => resendAlertNotification(a._id)}
                        >
                          {busy ? '…' : 'Resend'}
                        </button>
                      )}
                    </td>
                  </tr>
                );}) : (
                  <tr><td colSpan="8" style={{ textAlign:'center', color:'#aaa', padding:16 }}>No alerts in selected period</td></tr>
                )}
              </tbody>
            </table>
            <div style={{ marginTop:10, fontSize:11, color:'#888' }}>
              Report data sourced from MongoDB via REST API. Interacts with Sensor Data and Alerts.
            </div>
          </>
        )}
      </div>
    </div>
  );
}
